/**
 * Admin → New tenant form (Phase 35.2, super-admin only).
 *
 * Route: /admin/tenants/new
 * Submits to POST /api/admin/tenants → redirects to detail page.
 */
import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { provisionTenant } from "../services/adminTenants.js";
import { useStore } from "../store/useStore.js";

export default function TenantNew() {
  const [tenantId, setTenantId] = useState("");
  const [name, setName] = useState("");
  const [primaryDomain, setPrimaryDomain] = useState("");
  const [extraDomains, setExtraDomains] = useState("");
  const [adminEmail, setAdminEmail] = useState("");
  // Phase 43.2 — flipped to default CHECKED for v4.2.1. With the
  // bootstrap pipeline landing a drafted TenantProfile + ProductCatalogue
  // and flipping tenant status to `setup_review`, the happy path is:
  // create → bootstrap runs → tenant-admin reviews + activates. Leaving
  // bootstrap off means the tenant lands `active` with no profile/
  // catalogue, which is only appropriate for migration scenarios.
  const [bootstrap, setBootstrap] = useState(true);
  const [researchDomain, setResearchDomain] = useState("");
  const [skipAdminDomainCheck, setSkipAdminDomainCheck] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const pushToast = useStore((s) => s.pushToast);

  function normaliseDomain(raw) {
    try {
      // Accept full URLs (https://cursor.com/) or bare domains (cursor.com).
      const withScheme = raw.includes("://") ? raw : `https://${raw}`;
      return new URL(withScheme).hostname.replace(/^www\./, "").toLowerCase();
    } catch {
      return raw.trim().toLowerCase();
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError(null);
    const domains = [primaryDomain.trim(), ...extraDomains.split(",").map((d) => d.trim())]
      .filter(Boolean)
      .map(normaliseDomain);

    if (!tenantId.trim() || !name.trim() || domains.length === 0 || !adminEmail.trim()) {
      setError("All fields are required.");
      return;
    }

    setSubmitting(true);
    try {
      await provisionTenant({
        tenantId: tenantId.trim(),
        name: name.trim(),
        domains,
        adminEmail: adminEmail.trim(),
        bootstrap,
        skipAdminDomainCheck,
        ...(researchDomain.trim() ? { researchDomain: researchDomain.trim() } : {}),
      });
      pushToast("Provisioning started — tenant will be active shortly.");
      navigate(`/admin/tenants/${encodeURIComponent(tenantId.trim())}`);
    } catch (err) {
      setError(err.message);
      setSubmitting(false);
    }
  }

  return (
    <div className="admin-page">
      <div className="admin-page__header">
        <div>
          <Link to="/admin/tenants" className="admin-page__breadcrumb">
            ← Tenants
          </Link>
          <h1 className="admin-page__title">New Tenant</h1>
        </div>
      </div>

      <form className="admin-form" onSubmit={handleSubmit}>
        {error && (
          <div className="admin-form__error" role="alert">
            {error}
          </div>
        )}

        <label className="admin-form__label">
          Tenant ID
          <input
            className="admin-form__input"
            type="text"
            value={tenantId}
            onChange={(e) => setTenantId(e.target.value)}
            placeholder="e.g. tenant_acme"
            pattern="[a-z0-9_]+"
            required
          />
          <span className="admin-form__hint">
            Lowercase letters, numbers, underscores only. Cannot be changed.
          </span>
        </label>

        <label className="admin-form__label">
          Display name
          <input
            className="admin-form__input"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Acme Corp"
            required
          />
        </label>

        <label className="admin-form__label">
          Primary domain
          <input
            className="admin-form__input"
            type="text"
            value={primaryDomain}
            onChange={(e) => setPrimaryDomain(e.target.value)}
            placeholder="e.g. acme.com"
            required
          />
        </label>

        <label className="admin-form__label">
          Additional domains
          <input
            className="admin-form__input"
            type="text"
            value={extraDomains}
            onChange={(e) => setExtraDomains(e.target.value)}
            placeholder="Comma-separated, e.g. acmecorp.io, acme.co.uk"
          />
        </label>

        <label className="admin-form__label admin-form__label--inline">
          <input
            type="checkbox"
            checked={bootstrap}
            onChange={(e) => setBootstrap(e.target.checked)}
          />
          Run tenant bootstrap research
          <span className="admin-form__hint">
            Fires a background research pipeline (~2–5 min) that drafts a
            TenantProfile from the company&apos;s public site. Leave off in v4.2
            unless you know the tenant needs it — v4.2.1 will flip the
            default once ProductCatalogue drafting ships.
          </span>
        </label>

        <label className="admin-form__label">
          Research domain (optional)
          <input
            className="admin-form__input"
            type="text"
            value={researchDomain}
            onChange={(e) => setResearchDomain(e.target.value)}
            placeholder="Leave blank to use the primary domain"
          />
          <span className="admin-form__hint">
            Useful when the auth domain differs from the public marketing
            site (e.g. acme-internal.example.com for auth, acme.com for research).
          </span>
        </label>

        <label className="admin-form__label">
          Initial admin email
          <input
            className="admin-form__input"
            type="email"
            value={adminEmail}
            onChange={(e) => setAdminEmail(e.target.value)}
            placeholder="admin@acme.com"
            required
          />
        </label>

        <label className="admin-form__label admin-form__label--inline">
          <input
            type="checkbox"
            checked={skipAdminDomainCheck}
            onChange={(e) => setSkipAdminDomainCheck(e.target.checked)}
          />
          Admin will be invited later — skip domain check
          <span className="admin-form__hint">
            By default, the initial admin email must be on one of the declared
            domains. Tick this only when you&apos;re provisioning on behalf of
            the tenant and the real admin doesn&apos;t have a corporate email
            yet. The bypass is logged for audit.
          </span>
        </label>

        <div className="admin-form__actions">
          <Link to="/admin/tenants" className="btn-ghost">
            Cancel
          </Link>
          <button
            type="submit"
            className="btn-primary"
            disabled={submitting}
          >
            {submitting ? "Starting provisioning…" : "Provision tenant"}
          </button>
        </div>
      </form>
    </div>
  );
}
